// Custom Block Library — curated/built-in seed blocks. Now authored in SAFE TSL (declarative AST), exactly
// like user-published blocks — there is NO JavaScript anywhere in the library. App-bundled + immutable;
// each is parsed from canonical TSL text at load and MUST pass the TSL gate (scripts/test-block-gate-engine.mjs).

import type { Block } from '../../types';
import { BlockType } from '../../types';
import type { CuratedBlock, CustomBlockTemplate, CustomBlockVersion, ParameterSchema } from '../../types/customBlockLibrary';
import { parseBlock } from '../tslBlock/blockText';
import type { TslBlock } from '../tslBlock/ast';

const T = (publicVisible: boolean, aiSafe: boolean, optimizerSafe: boolean) => ({ publicVisible, aiSafe, optimizerSafe });

/** Parse canonical TSL text → AST at load; throw loudly if a curated block is malformed (caught in CI). */
function tsl(text: string): TslBlock { const r = parseBlock(text); if (!r.ok) throw new Error(`curated TSL parse error: ${JSON.stringify(r.errors)}`); return r.block; }

interface Seed { id: string; version: number; tier: ReturnType<typeof T>; name: string; description: string; tags: string[]; riskNotes?: string; example?: string; text: string; }

const SEEDS: Seed[] = [
  {
    id: 'builtin:tema', version: 1, tier: T(true, true, true), name: 'TEMA', description: 'Triple EMA — a low-lag trend line.',
    tags: ['trend', 'ema', 'tema'], riskNotes: 'Lower lag than EMA but can overshoot in choppy markets.', example: 'Use as a fast/slow crossover, or as a trend filter (price above TEMA = uptrend).',
    text: `indicator {
  param period: int 2..200 = 12
  value: ((3 * (ema(close, period) - ema(ema(close, period), period))) + ema(ema(ema(close, period), period), period))
}`,
  },
  {
    id: 'builtin:tsi', version: 1, tier: T(true, true, true), name: 'TSI', description: 'True Strength Index — double-smoothed momentum oscillator.',
    tags: ['momentum', 'tsi', 'oscillator'], riskNotes: 'Oscillator centred on 0; not a price overlay.', example: 'Gate longs on TSI > 0.',
    text: `indicator {
  param longLen: int 2..200 = 25
  param shortLen: int 2..100 = 13
  value: ((ema(ema(abs(change(close, 1)), longLen), shortLen) == 0) ? 0 : ((100 * ema(ema(change(close, 1), longLen), shortLen)) / ema(ema(abs(change(close, 1)), longLen), shortLen)))
}`,
  },
  {
    id: 'builtin:above-ema-filter', version: 1, tier: T(true, true, true), name: 'Above EMA filter', description: 'Passes when close is above its EMA (a trend/regime gate).',
    tags: ['filter', 'regime', 'ema'], riskNotes: 'Whipsaws in ranging markets; pair with a momentum gate.', example: 'AND this filter with an entry trigger to only take trades with trend.',
    text: `filter {
  param period: int 2..400 = 50
  value: ema(close, period)
  signal: (close > ema(close, period))
}`,
  },
  {
    id: 'builtin:fixed-fractional', version: 1, tier: T(true, true, true), name: 'Fixed-fractional risk', description: 'Risk a fixed % of equity per trade, sized off the stop distance.',
    tags: ['sizing', 'risk'], riskNotes: 'Position scales with equity AND with stop distance — a tight stop ⇒ a larger position.', example: 'Wire to an entry; set Risk % (e.g. 1%). Needs a stop distance from your SL logic.',
    text: `qty_calculator {
  param riskPct: float 0.1..5 = 1
  qty: ((equity * (riskPct / 100)) / stopDistance)
}`,
  },
  {
    id: 'builtin:max-bars', version: 1, tier: T(true, true, true), name: 'Max bars in trade', description: 'Flatten a position after N bars (a time stop). Causal.',
    tags: ['risk', 'time-stop'], riskNotes: 'Risk rules can flatten positions — review carefully.', example: 'Add to a strategy to cap how long any trade can stay open.',
    text: `risk_rule {
  param maxBars: int 1..500 = 50
  risk: ((barsSinceEntry >= maxBars) ? 2 : 0)
}`,
  },
];

export const CURATED_BLOCKS: CuratedBlock[] = SEEDS.map((s) => {
  const tslAst = tsl(s.text);
  return {
    id: s.id, version: s.version, tier: s.tier, category: tslAst.category, blockType: tslAst.blockType,
    name: s.name, description: s.description, tags: s.tags, riskNotes: s.riskNotes, example: s.example,
    tslAst, paramSchema: tslAst.params,
  };
});

function defaultsOf(schema: ParameterSchema): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(schema)) out[k] = v.default;
  return out;
}

export function findCurated(id: string): CuratedBlock | undefined { return CURATED_BLOCKS.find((b) => b.id === id); }

/** Curated blocks as library head rows (for the gallery list). */
export function curatedTemplates(): CustomBlockTemplate[] {
  return CURATED_BLOCKS.map((b) => ({
    id: b.id, authorId: 'builtin', source: 'builtin', category: b.category, blockType: b.blockType,
    name: b.name, description: b.description, tags: b.tags, latestVersion: b.version,
    publicVisible: b.tier.publicVisible, aiSafe: b.tier.aiSafe, optimizerSafe: b.tier.optimizerSafe,
    status: 'published', downloads: 0,
  }));
}

/** The TSL AST + schema for a curated block version (for the detail drawer + insert). */
export function curatedVersion(id: string): CustomBlockVersion | undefined {
  const b = findCurated(id); if (!b) return undefined;
  return { blockId: b.id, version: b.version, tslAst: b.tslAst, paramSchema: b.paramSchema, codeSha256: '' };
}

/** Build a CUSTOM Block from a curated library block, stamping provenance (insert-from-library). */
export function buildLibraryBlock(c: CuratedBlock, blockId = `cb_${c.id.replace(/[^a-z0-9]+/gi, '_')}_v${c.version}`): Block {
  return {
    id: blockId,
    type: BlockType.CUSTOM,
    position: { x: 100, y: 100 },
    data: {
      name: c.name,
      blockType: c.blockType,
      tslAst: c.tslAst,
      parameters: defaultsOf(c.paramSchema),
      libraryBlockId: c.id,
      libraryVersion: c.version,
      librarySource: 'builtin',
    },
    connections: { inputs: [], outputs: [] },
  } as Block;
}
